import React, { useContext, useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { Direction, nodeType, workflow, WorkflowLink } from "./start";
import { formatNumber, getHardcodedColor, getHardcodedLabels } from "../../../utils";
import { nodeTypes, scoreTypes } from "../../../types";
import { getInfo } from "../../Info/infos";
import { Card, Image, Text } from "@mantine/core";
import UiStateContext from "../../../providers/UiStateProvider";

const WorkflowDiagram = ({ data, alpha, w, onLinkSelected, onSaturationSelected, width, height }) => {
    const [labelsHeight, graphHeight] = [height * 0.2, height * 0.8]
    const svgRef = useRef();
    const svgLabelsRef = useRef();
    const [tooltip, setTooltip] = useState<{ x: number; y: number; text: string } | null>(null);
    const [selectedLinks, setSelectedLinks] = useState([])
    const [selectedSaturation, setSelectedSaturation] = useState<{ [key: string]: number }>(null)
    const [hoveredLabel, setHoveredLabel] = useState(null);
    const [labelPosition, setLabelPosition] = useState(null);
    const containerRef = React.useRef();
    const { infoCardOpened, setInfoCardOpened } = useContext(UiStateContext)

    useEffect(() => {
        onLinkSelected(selectedLinks)
    }, [selectedLinks])

    useEffect(() => {
        onSaturationSelected(selectedSaturation)
    }, [selectedSaturation])

    useEffect(() => {
        setSelectedLinks([])
        setSelectedSaturation(null)
    }, [data, alpha, w])

    const isSelectedLink = d => selectedLinks.find(l => d.id === l.id)
    const colorLink = d => d.source.type === nodeType.gene ? getHardcodedColor(nodeTypes.gene) : (d.source.type === nodeType.sum || d.source.type === nodeType.score) ? getHardcodedColor(scoreTypes.mscore) : getHardcodedColor(d.source.base)

    useEffect(() => {
        function drawLink(link: WorkflowLink) {
            const rect1 = link.source, rect2 = link.target
            return `M${rect1.x + rect1.width + 10},${rect1.y + link.start.offset}
                    L${rect1.x + rect1.width},${rect1.y + link.start.offset}
                    L${rect1.x + rect1.width},${rect1.y + link.start.offset + link.start.height}
                    L${rect1.x + rect1.width + 10},${rect1.y + link.start.offset + link.start.height}
                    L${rect2.x - 10},${rect2.y + link.end.offset + link.end.height}
                    L${rect2.x},${rect2.y + link.end.offset + link.end.height}
                    L${rect2.x},${rect2.y + link.end.offset}
                    L${rect2.x - 10},${rect2.y + link.end.offset}
                    Z`;
        }

        function drawClosedLink(link: WorkflowLink) {
            const rect1 = link.source, rect2 = link.target
            const curveWidth = link.source.height
            return `
                    M ${rect1.x + rect1.width} ${rect1.y + link.start.offset}  
                    v ${link.start.height}  
                    C ${rect1.x + rect1.width + curveWidth / 2} ${rect1.y + link.start.offset + 2 * link.start.height * 0.2}, 
                        ${rect1.x + rect1.width + curveWidth / 2} ${rect1.y + link.start.offset + 2 * link.start.height * 0.8}, 
                        ${rect1.x + rect1.width} ${rect1.y + link.start.offset + 2 * link.start.height - link.start.height}  
                    v ${link.start.height}  
                    C ${rect1.x + rect1.width + curveWidth * 0.8} ${rect1.y + link.start.offset + 2 * link.start.height}, 
                        ${rect1.x + rect1.width + curveWidth * 0.8} ${rect1.y + link.start.offset}, 
                        ${rect1.x + rect1.width} ${rect1.y + link.start.offset}  
                    Z
                    `;
        }

        function drawArrowLink(link: WorkflowLink) {
            const rect1 = link.source, rect2 = link.target
            const startY = rect1.y + link.start.offset, endY = rect2.y + link.end.offset
            const heightDiff = startY - endY;
            // const curveHeight = heightDiff * 1.1;

            // return `M ${rect1.x + rect1.width} ${startY} 
            //     Q ${(rect1.x + rect1.width + rect2.x) / 2} ${startY - curveHeight}, 
            //         ${rect2.x} ${endY}`; 

            return `M ${rect1.x + rect1.width} ${startY} 
                L ${rect2.x} ${endY}`;
        }

        const svg = d3.select(svgRef.current);

        svg.append("rect")
            .attr("width", "100%")
            .attr("height", "100%")
            .attr("fill", "white");


        const { nodes, links, stages } = workflow(data, alpha, w, width, graphHeight);

        const maxSaturationInput = Math.max(...nodes.filter(n => n.type === nodeType.saturation).map(n => n.input))

        const getTooltipPosition = (event: MouseEvent) => {
            const boundingBox = event.target.getBoundingClientRect();
            const containerRect = containerRef.current.getBoundingClientRect();
            return {
                x: boundingBox.left - containerRect.left + 10,
                y: boundingBox.top - containerRect.top + 10,
            };
        };

        svg.append('defs')
            .append('pattern')
            .attr('id', 'diagonalHatch')
            .attr('patternUnits', 'userSpaceOnUse')
            .attr('width', 10)
            .attr('height', 10)
            .append('path')
            .attr('d', 'M0,0 l10,10')
            .attr('stroke', '#000')
            .attr('stroke-width', 1);

        // Define arrow marker
        svg.append("defs").append("marker")
            .attr("id", "arrow")
            .attr("viewBox", "0 0 10 10")
            .attr("refX", 10)
            .attr("refY", 5)
            .attr("markerWidth", 6)
            .attr("markerHeight", 6)
            .attr("orient", "auto")
            .append("path")
            .attr("d", "M 0 0 L 10 5 L 0 10 Z")
            .attr("fill", "grey");

        // Draw nodes
        const node = svg.append("g")
            .selectAll("g")
            .data(nodes)
            .enter()
            .append("g")
            .attr("class", "node");

        node.append("rect")
            .attr("x", d => d.x)
            .attr("y", d => d.y)
            .attr("height", d => d.height)
            .attr("width", d => d.width)
            .style("fill", d => d.type === nodeType.gene ? getHardcodedColor(nodeTypes.gene) : (d.type === nodeType.sum || d.type === nodeType.score) ? getHardcodedColor(scoreTypes.mscore) : d.type === nodeType.saturation && (selectedSaturation === null || selectedSaturation.id !== d.id) ? '#fff' : getHardcodedColor(d.base))
            .style("stroke", d => d.type === nodeType.saturation ? getHardcodedColor(d.base) : "black")
            .style("stroke-width", d => d.type === nodeType.saturation ? "2" : "0.5")
            .style("opacity", d => d.type !== nodeType.saturation && d.direction === Direction.down ? "0.5" : "1")
            .style("fill-opacity", d => d.type === nodeType.saturation && selectedSaturation !== null && selectedSaturation.id === d.id ? '0.2' : '1')
            .on("mouseover", (event, d) => {
                if(d.type === nodeType.gene) return
                if (d.hint!) setTooltip({ ...getTooltipPosition(event), text: formatNumber(d.hint) });
                if (d.type === nodeType.saturation)
                    d3.select(event.target).style("fill", getHardcodedColor(d.base)).style("fill-opacity", "0.2");
            })
            .on("mousemove", (event) => {
                setTooltip(prev => prev ? { ...prev, ...getTooltipPosition(event) } : null);
            })
            .on("mouseout", (event, d) => {
                setTooltip(null);
                if (d.type === nodeType.saturation && (selectedSaturation === null || selectedSaturation.id !== d.id))
                    d3.select(event.target).style("fill", '#fff').style("fill-opacity", "1");
            })
            .on("click", function (event, d) {
                if (d.type !== nodeType.saturation) return;
                setSelectedSaturation(prevSaturation => {
                    if ((prevSaturation !== null) && (prevSaturation.id === d.id)) {
                        return null
                    } else {
                        return { id: d.id, input: d.input, alpha: d.alpha, xMax: maxSaturationInput, base: d.base };
                    }
                })
            });

        node.each(function (d) {
            const rect = d3.select(this);
            if (d.type !== nodeType.saturation && d.direction === Direction.down)
                rect.append('rect')
                    .attr('x', d.x)
                    .attr('y', d.y)
                    .attr('height', d.height)
                    .attr('width', d.width)
                    .style('fill', 'url(#diagonalHatch)')
                    .style('stroke', 'none')
                    .on("mouseover", (event, d) => {
                        if (d.hint!) setTooltip({ ...getTooltipPosition(event), text: d.hint });
                    })
                    .on("mousemove", (event) => {
                        setTooltip(prev => prev ? { ...prev, ...getTooltipPosition(event) } : null);
                    })
                    .on("mouseout", () => {
                        setTooltip(null);
                    })
                    .on("click", function (event, d) {
                        if (d.type !== nodeType.saturation) return;
                        setSelectedSaturation(prevSaturation => {
                            if ((prevSaturation !== null) && (prevSaturation.id === d.id)) {
                                return null
                            } else {
                                return { id: d.id, input: d.input, alpha: d.alpha, xMax: maxSaturationInput, base: d.base };
                            }
                        })
                    });
        })

        node.append('text')
            .attr('x', d => d.type !== nodeType.score ? 6 + d.x + d.width : d.x - d.width - 60)
            .attr('y', d => d.y + (d.height) / 2)
            .attr('dy', '.35em')
            .attr('text-anchor', 'end')
            .text(d => d.label! || '')
            .attr('text-anchor', 'start');

        // Draw links
        const link = svg.append("g")
            .selectAll("g")
            .data(links)
            .enter()
            .append("g")
            .attr("class", "node")

        link.append("path")
            .attr("d", d => d.type! === 'wasted' ? drawClosedLink(d) : d.type! === 'arrow' ? drawArrowLink(d) : drawLink(d))
            .attr("stroke", 'black')
            .attr("stroke-width", 1)
            // .attr("fill", d => d.type! === 'arrow' ? 'none' : isSelectedLink(d)? colorLink(d) : "gray")
            .attr("fill", d => {
                // Check if the link is selected
                const isSelected = selectedLinks.find(l => l.id === d.id);
                return isSelected ? colorLink(d) : "gray"; // Use colorLink if selected
            })
            .attr("marker-end", d => d.type! !== 'arrow' ? 'none' : "url(#arrow)")
            .attr("opacity", d => d.type! === 'arrow' ? 'none' : isSelectedLink(d) ? '0.5' : '0.2')
            .on("mouseover", function (event, d) {
                d3.select(this).style("fill", d => colorLink(d));
            })
            .on("mouseout", function (event, d) {
                const isSelected = selectedLinks.find(l => l.id === d.id);
                d3.select(this).style("fill", isSelected ? colorLink(d) : "gray");
            })
            .on("click", function (event, d) {
                if(d.source.type !== nodeType.gene) return;
                setSelectedLinks(prevLinks => {
                    const isSelected = prevLinks.find(l => l.id === d.id);
                    if (isSelected) {
                        return prevLinks.filter(l => l.id !== d.id);  // Remove link
                    } else {
                        return [...prevLinks, d];  // Add link
                    }
                })
            });

        const svgLabels = d3.select(svgLabelsRef.current);

        stages.forEach(stage => {
            const middleX = (stage.start + stage.end) / 2;
            const middleY = labelsHeight / 2;

            svgLabels.append("text")
                .attr("x", middleX)
                .attr("y", middleY)
                .attr("dy", ".35em")
                .attr("text-anchor", "middle")
                .text(getHardcodedLabels(stage.stage))
                .style("font-size", "14px")
                .style("fill", "black").on("mouseenter", (event) => {
                    setHoveredLabel(stage.stage);
                    const boundingBox = event.target.getBoundingClientRect();
                    const containerRect = containerRef.current.getBoundingClientRect();
                    setLabelPosition({
                        x: boundingBox.left - containerRect.left + 10,
                        y: boundingBox.top - containerRect.top + 10,
                    });

                    if (!infoCardOpened.includes(stage.stage))
                        setInfoCardOpened([...infoCardOpened, stage.stage])
                })
                .on("mouseleave", () => {
                    setHoveredLabel(null);
                    setLabelPosition(null);
                });

            if (!infoCardOpened.includes(stage.stage))
                svgLabels.append("circle")
                    .attr("cx", middleX)
                    .attr("cy", labelsHeight / 2 - 15)
                    .attr("r", 4)
                    .style("fill", "#f03e3e");
        });

        return () => {
            svg.selectAll('*').remove();
            svgLabels.selectAll("*").remove();
        };

    }, [data, alpha, w, selectedLinks, width, height, selectedSaturation, infoCardOpened]);

    return (
        <div
            ref={containerRef}
            style={{
                position: "relative",
                height: height,
                width: width,
            }}
        >
            <svg ref={svgLabelsRef} width={width} height={labelsHeight} style={{ cursor: 'pointer' }} />
            <svg
                style={{ cursor: 'pointer' }}
                ref={svgRef}
                width={width}
                height={graphHeight}
            />
            {tooltip && (
                <div
                    style={{
                        position: "absolute",
                        left: tooltip.x + 10,
                        top: tooltip.y + 10,
                        background: "white",
                        border: "1px solid black",
                        padding: "5px",
                        fontSize: "12px",
                        pointerEvents: "none"
                    }}
                >
                    {tooltip.text}
                </div>
            )}
            {hoveredLabel && labelPosition &&
                <Card shadow="lg" padding="lg" radius="md" withBorder={true} style={{
                    position: "absolute",
                    left: labelPosition.x + 10,
                    top: labelPosition.y + 10,
                    maxWidth: width / 4
                }}>
                    <Card.Section>
                        <Image src={getInfo(hoveredLabel).image} />
                    </Card.Section>
                    <Text fw={500}>{getInfo(hoveredLabel).title}</Text>
                    <Text size="sm" c="dimmed" style={{
                        whiteSpace: 'pre-line',
                    }}>{getInfo(hoveredLabel).info}</Text>
                </Card>
            }
        </div>
    );
};

export default WorkflowDiagram;
